import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { images } from "src/assets/images/images";
import { useCustomNavigate } from "src/hooks/useCustomNavigate";
import { useIsDigitalOrDigitalAgent } from "src/hooks/useIsDigitalOrDigitalAgent";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import { checkFXEligibility } from "src/redux/slices/appSlice/AppApi";
import {
  setAgentId,
  setBusinessUnit,
  setCreatedBy,
  setEncryptedPersonalId,
  setExperimentData,
  setGuid,
  setIsLoading,
  setPersonalId,
  setTravelPolicyNumber,
} from "src/redux/slices/appSlice/AppSlice";
import { setPopupInfo } from "src/redux/slices/popupSlice/popupSlice";
import {
  API_RESULT,
  ROUTES,
  URL_SOURCE_TYPE,
  POPUP_TYPE,
  BusinessUnit,
  CHANNELS,
} from "src/utils/enums";
import {
  CheckFxEligibilityRequest,
  ExperimentData,
  UpdateOnboardingAnalytics,
} from "src/utils/types";
import {
  getChannelId,
  handleResponseCode,
  logPfsAnalyticsEvent,
  sentPostMessage,
} from "src/utils/utils";

const EligibilityRouter: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    strings,
    fXEligibilityResStatus,
    fXEligibilityData,
    sessionId,
    hostUrl,
    fXEligibilityErrorData,
    mockDataEnabled,
  } = useAppSelector((state) => state.app);
  const { navigateTo } = useCustomNavigate();
  const isDigitalOrAgent = useIsDigitalOrDigitalAgent();
  const currentSource = location.pathname.split("/")[1].toLocaleLowerCase();
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Ensure the message is coming from a trusted source
      // if (event.origin !== "https://pfs-card-display-dev.passportcard.com/")
      //   return;

      const {
        personalId,
        businessUnit,
        guid,
        travelPolicyNumber,
        agentId,
        salesExperimentID,
        experimentName,
        salesExperimentVersionID,
        versionName,
        experimentType,
      } = event.data;

      if (personalId && businessUnit && guid) {
        // dispatch(setIsLoading(false));
        const data: CheckFxEligibilityRequest = {
          businessUnit,
          guid: guid,
          personalId: personalId,
          travelPolicyNumber: travelPolicyNumber ?? "",
          agentId: agentId ?? "",
          salesExperimentID: salesExperimentID ?? "",
          experimentName: experimentName ?? "",
          salesExperimentVersionID: salesExperimentVersionID ?? "",
          versionName: versionName ?? "",
          experimentType: experimentType ?? "",
          channel: isDigitalAgent ? CHANNELS.DIGITAL_AGENT : CHANNELS.DIGITAL,
        };
        updateStoreWithReceivedDataFromURL(
          travelPolicyNumber,
          businessUnit,
          "",
          agentId,
          guid,
          personalId
        );
        updateStoreWithExperimentData({
          salesExperimentID,
          experimentName,
          salesExperimentVersionID,
          versionName,
          experimentType,
        });
        dispatch(checkFXEligibility(data));
      }

    };
    const fullUrl = window.location.href.toLowerCase();
    const isDigitalAgent = fullUrl.includes(URL_SOURCE_TYPE.DIGITAL_AGENT);
    const isDigital = fullUrl.includes(URL_SOURCE_TYPE.DIGITAL) && !isDigitalAgent;

    if (isDigitalAgent || isDigital) {
      window.addEventListener("message", handleMessage);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [dispatch, hostUrl]);
  const updateStoreWithExperimentData = (data: ExperimentData) => {
    dispatch(setExperimentData(data));
  };
  const updateStoreWithEncryptedPersonalId = (encryptedPersonalId: string) => {
    dispatch(setEncryptedPersonalId(encryptedPersonalId));
  };
  const updateStoreWithReceivedDataFromURL = (
    travelPolicyNumber: string,
    businessUnit: string,
    createdBy: string = "",
    agentId: string = "",
    guid: string = "",
    personalId: string = ""
  ) => {
    dispatch(setTravelPolicyNumber(travelPolicyNumber));
    dispatch(setAgentId(agentId));
    dispatch(setGuid(guid));
    dispatch(setBusinessUnit(businessUnit as BusinessUnit));
    dispatch(setPersonalId(personalId));
    dispatch(setCreatedBy(createdBy));
  };

  useEffect(() => {
    dispatch(setIsLoading(true));

    // Retrieve the query parameter using URLSearchParams
    const urlParams = new URLSearchParams(window.location.search);
    let personalIdEncoded = urlParams.get("personalId") || "";

    // Decode the parameter and convert any spaces (that replaced '+') back to '+'
    let personalId = decodeURIComponent(personalIdEncoded).replace(/ /g, "+");
    const userParam = new URLSearchParams(window.location.search).get(
      "user"
    );
    // Replace the Cyrillic З with the Latin 3 in case it was introduced by mistake
    personalId = personalId.replace(/З/g, "3");
    // add simple delay of 2 seconds dont use await

    const params = new URLSearchParams(location.search);
    const businessUnit = params.get("businessUnit");
    const travelPolicyNumber = params.get("travelPolicyNumber");
    const createdBy = params.get("createdBy");

    if (personalId && strings && hostUrl) {

      const data: CheckFxEligibilityRequest = {
        businessUnit: businessUnit,
        travelPolicyNumber: travelPolicyNumber ?? "",
        channel: getChannelId(hostUrl),
        personalId: personalId,
        userId: userParam ?? "",
      };
      updateStoreWithReceivedDataFromURL(
        travelPolicyNumber ?? "",
        businessUnit ?? "",
        createdBy ?? "",
        "",
        "",
        ""
      );
      updateStoreWithEncryptedPersonalId(personalId);

      dispatch(checkFXEligibility(data));
    } else {
      const currentSource = location.pathname
        .split("/")[1]
        ?.toLocaleLowerCase();
      switch (currentSource) {
        case URL_SOURCE_TYPE.MARKETING:
        case URL_SOURCE_TYPE.SMS:
        case URL_SOURCE_TYPE.CAMPAIGN:
        case URL_SOURCE_TYPE.REP_MANUAL:
        case URL_SOURCE_TYPE.APP:
          if (currentSource == URL_SOURCE_TYPE.REP_MANUAL) {
            const urlParams = new URLSearchParams(window.location.search);
            let user = urlParams.get("user") || "";
            dispatch(setAgentId(user));
          }
          dispatch(setIsLoading(false));
          navigateTo(ROUTES.LOGIN);
          break;

        default:
          break;
      }
    }

  }, [mockDataEnabled, hostUrl]);

  useEffect(() => {
    switch (fXEligibilityResStatus) {
      case API_RESULT.SUCCEEDED:
        dispatch(setIsLoading(false));

        const isSuccess = fXEligibilityData?.responseCode === 0;
        if (isSuccess) {
          if (currentSource && currentSource.trim().length > 0) {
            switch (currentSource) {
              case URL_SOURCE_TYPE.ENSURE:
                dispatch(
                  setPopupInfo({
                    show: true,
                    type: POPUP_TYPE.LEADS_TRANSFERRING_POPUP,
                    title: strings.t_error_popup_cant_move_title,
                    subTitle: "",
                    body: strings.t_error_popup_cant_move_body,
                    okBtnText: strings.t_error_popup_cant_find_button,
                    img: images.image2,
                    mobileImg: images.image2Mobile,
                    withButton: true,
                    hideCloseBtn: true,
                  })
                );
                break;
              case URL_SOURCE_TYPE.PFS_REP:
                //take "user" parameter from URL
                const urlParams = new URLSearchParams(window.location.search);
                let user = urlParams.get("user") || "";
                dispatch(setAgentId(user));

                navigateTo(ROUTES.PC_PLUS_SCREEN);
                break;
              case URL_SOURCE_TYPE.DIGITAL:
                navigateTo(ROUTES.UPDATE_CONTACT_DETAILS);
                break;
              case URL_SOURCE_TYPE.DIGITAL_AGENT:
                // show 2 options screen
                dispatch(
                  setPopupInfo({
                    show: true,
                    type: POPUP_TYPE.DIGITAL_AGENT,
                    title: strings.t_digiral_agent_title,
                    body: strings.t_digiral_agent_body,
                    img: images.image1,
                    mobileImg: images.image1Mobile,
                    strings,
                    hideCloseBtn: false,
                  })
                );
                break;
              default:
                break;
            }
          }
        } else {
          handleResponseCode(
            fXEligibilityData,
            currentSource,
            hostUrl,
            isDigitalOrAgent,
            strings
          );
        }

        dispatch(setIsLoading(false));

        break;
      case API_RESULT.FAILED:
        dispatch(setIsLoading(false));
        const responseCode =
          fXEligibilityErrorData?.data?.responseData?.responseCode;

        if (responseCode == -1) {
          handleFxEligibilityError();
        } else {
          handleResponseCode(
            fXEligibilityErrorData?.data?.responseData,
            currentSource,
            hostUrl,
            isDigitalOrAgent,
            strings
          );
        }
        break;

      default:
        break;
    }
  }, [fXEligibilityResStatus]);

  const handleFxEligibilityError = () => {
    if (fXEligibilityData) {
      logPfsAnalyticsEvent({
        error_popup: true,
      } as UpdateOnboardingAnalytics);
    }

    dispatch(
      setPopupInfo({
        show: true,
        type: POPUP_TYPE.WARNING,
        title: strings.t_error_popup_cant_move_title,
        subTitle: "",
        body: strings.t_error_popup_cant_move_body,
        okBtnText: strings.t_error_popup_cant_find_button,
        img: images.agent,
        mobileImg: images.errMobile,
        withButton: false,
        hideCloseBtn: true,
      })
    );
    if (hostUrl && isDigitalOrAgent) {
      const data = {
        status: API_RESULT.FAILED,
        responseCode: fXEligibilityData?.responseCode?.toString(),
        collectCardInstruction: fXEligibilityData?.collectCardInstruction,
        passportCard4LastDigits: fXEligibilityData?.passportCard4LastDigits,
      };
      sentPostMessage(data);
    }
    dispatch(setIsLoading(false));
  };

  return <div></div>;
};

export default EligibilityRouter;
